'use client';
import React, { useEffect, useRef, useState } from 'react';

const STATS = [
  { value: '2.4M+', label: 'Videos Generated' },
  { value: '94%', label: 'Avg Viral Score' },
  { value: '180+', label: 'Countries' },
  { value: '12x', label: 'Avg Reach Boost' },
];

const FEATURES = [
  { icon: '🔥', title: 'Trend Detection', desc: 'Real-time signals from TikTok, YouTube & Google Trends' },
  { icon: '🤖', title: 'AI Script Builder', desc: 'GPT-powered hooks, scripts, and captions in seconds' },
  { icon: '🎬', title: 'Auto Video Gen', desc: 'AI video generation pipeline — no editing required' },
  { icon: '📅', title: 'Auto Scheduler', desc: 'Post at peak times across all platforms automatically' },
  { icon: '📊', title: 'Viral Score Engine', desc: 'Predict virality before you post with our scoring model' },
  { icon: '⚡', title: 'Queue System', desc: 'Process hundreds of videos in parallel with job queuing' },
];

export default function V3HeroSection() {
  const [mounted, setMounted] = useState(false);
  const [viralScore, setViralScore] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    setMounted(true);
    // Animate viral score counter
    let frame = 0;
    const target = 87;
    const timer = setInterval(() => {
      frame += 2;
      setViralScore(Math.min(frame, target));
      if (frame >= target) clearInterval(timer);
    }, 20);
    return () => clearInterval(timer);
  }, []);

  // Animated particle grid
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; opacity: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        opacity: Math.random() * 0.4 + 0.1,
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(194,24,91,${p.opacity})`;
        ctx.fill();
      });
      // Draw connections
      particles.forEach((a, i) => {
        particles.slice(i + 1).forEach((b) => {
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(194,24,91,${0.08 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, [mounted]);

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-24 pb-16 px-6"
      style={{ background: 'var(--blackout)' }}
    >
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.6 }}
      />

      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(194,24,91,0.12) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-6"
          style={{ background: 'rgba(194,24,91,0.12)', color: 'var(--ruby)', border: '1px solid rgba(194,24,91,0.3)' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
          ViralForge AI V3 — Automation Engine
        </div>

        {/* Headline */}
        <h1
          className="text-5xl md:text-7xl font-bold font-display leading-tight mb-6"
          style={{ color: 'var(--chrome)' }}
        >
          Create viral TikTok &{' '}
          <span style={{ color: 'var(--ruby)' }}>Instagram</span>{' '}
          content in seconds
        </h1>

        <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto" style={{ color: 'var(--chrome-dim)' }}>
          Generate hooks, scripts, captions, and carousel posts engineered for reach and engagement.
          Now with AI video generation, trend detection, and auto-scheduling.
        </p>

        {/* CTA buttons */}
        <div className="flex flex-wrap gap-4 justify-center mb-16">
          <a
            href="#generate"
            className="btn-ruby px-8 py-4 rounded-xl text-base font-semibold animate-ruby-pulse"
          >
            <span>⚡ Start Free</span>
          </a>
          <a
            href="#examples"
            className="btn-chrome px-8 py-4 rounded-xl text-base font-semibold"
          >
            See Examples
          </a>
        </div>

        {/* Live viral score preview */}
        {mounted && (
          <div
            className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl mb-16"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(194,24,91,0.2)' }}
          >
            <div className="text-left">
              <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--chrome-dim)' }}>Live Viral Score</p>
              <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>fat loss hack 2026</p>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-24 h-2 rounded-full overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.08)' }}
              >
                <div
                  className="h-full rounded-full transition-all duration-75"
                  style={{
                    width: `${viralScore}%`,
                    background: 'linear-gradient(90deg, var(--ruby-dark), var(--ruby))',
                    boxShadow: '0 0 8px rgba(194,24,91,0.6)',
                  }}
                />
              </div>
              <span className="text-lg font-bold font-mono" style={{ color: 'var(--ruby)' }}>
                {viralScore}
              </span>
              <span className="text-xs font-semibold" style={{ color: 'var(--ruby)' }}>VIRAL</span>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {STATS.map((stat, i) => (
            <div
              key={i}
              className="p-4 rounded-2xl text-center"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <p className="text-2xl font-bold font-mono" style={{ color: 'var(--chrome)' }}>{stat.value}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--chrome-dim)' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Problem section */}
      <div className="relative z-10 max-w-5xl mx-auto w-full mb-20">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl font-bold font-display mb-6" style={{ color: 'var(--chrome)' }}>
              Why most creators don't grow
            </h2>
            <div className="space-y-3">
              {[
                'They run out of content ideas',
                'Their hooks don\'t grab attention',
                'They post without strategy',
                'They rely on guesswork instead of systems',
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <span style={{ color: 'var(--ruby)' }}>✗</span>
                  <span className="text-sm" style={{ color: 'var(--chrome-dim)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-3xl font-bold font-display mb-6" style={{ color: 'var(--chrome)' }}>
              Meet your AI viral content engine
            </h2>
            <div className="space-y-3">
              {[
                'Detect trending topics in real-time',
                'Generate scroll-stopping hooks automatically',
                'Build complete video scripts in seconds',
                'Schedule posts at peak engagement times',
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ background: 'rgba(194,24,91,0.05)', border: '1px solid rgba(194,24,91,0.15)' }}
                >
                  <span style={{ color: 'var(--ruby)' }}>✓</span>
                  <span className="text-sm" style={{ color: 'var(--chrome)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features bento grid */}
      <div className="relative z-10 max-w-5xl mx-auto w-full mb-20" id="examples">
        <h2 className="text-3xl font-bold font-display text-center mb-8" style={{ color: 'var(--chrome)' }}>
          The full automation stack
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="p-5 rounded-2xl transition-all"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                gridColumn: i === 0 ? 'span 2' : undefined,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(194,24,91,0.3)';
                e.currentTarget.style.background = 'rgba(194,24,91,0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
              }}
            >
              <span className="text-2xl mb-3 block">{f.icon}</span>
              <h3 className="text-base font-bold mb-1" style={{ color: 'var(--chrome)' }}>{f.title}</h3>
              <p className="text-sm" style={{ color: 'var(--chrome-dim)' }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Example output */}
      <div
        className="relative z-10 max-w-2xl mx-auto w-full p-6 rounded-2xl mb-20"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(194,24,91,0.2)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ color: 'var(--chrome)' }}>Example Output</h3>
          <span
            className="text-xs px-2.5 py-1 rounded-full font-semibold"
            style={{ background: 'rgba(194,24,91,0.15)', color: 'var(--ruby)' }}
          >
            Score: 87 · Viral
          </span>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--chrome-dim)' }}>🎣 Hook</p>
            <p className="text-sm p-3 rounded-xl" style={{ background: 'rgba(194,24,91,0.08)', color: 'var(--chrome)', border: '1px solid rgba(194,24,91,0.15)' }}>
              "You're losing fat the wrong way…"
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--chrome-dim)' }}>📝 Script</p>
            <p className="text-sm p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', color: 'var(--chrome-dim)', border: '1px solid rgba(255,255,255,0.06)' }}>
              15–30 sec viral breakdown with retention hooks at 3s, 10s, and 25s...
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--chrome-dim)' }}>💬 Caption</p>
            <p className="text-sm p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', color: 'var(--chrome-dim)', border: '1px solid rgba(255,255,255,0.06)' }}>
              Most people don't know this... Save this before it gets taken down 👇
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
